import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useProfile } from "../../context/userContext";
import { useLocation } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

/**
 * RegisterRoute - Two-stage route guard for /register
 *
 * Stage 1: Check if user is authenticated (Auth0)
 *   - If NOT authenticated AND no Auth0 redirect params → Redirect to home
 *   - If Auth0 redirect params present → Allow access (user in middle of Auth0 flow)
 *
 * Stage 2: Check if user already has a profile
 *   - If HAS profile → Redirect to / (already registered, skip registration)
 *   - If NO profile → Allow access to /register (first-time signup)
 *
 * This ensures /register is ONLY accessible during initial signup flow
 * and prevents already-registered users from accessing it again.
 */
const RegisterRoute = ({ children }) => {
    const { authenticated } = useAuth();
    const { profile, loading } = useProfile();
    const location = useLocation();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const checkReady = async () => {
            // Wait for authentication and profile to be loaded
            if (!loading) {
                await new Promise(resolve => setTimeout(resolve, 500));
                setIsReady(true);
            }
        };
        checkReady();
    }, [loading]);

    // Check if this is an Auth0 action redirect (has session_token and state)
    const searchParams = new URLSearchParams(location.search);
    const isAuth0Redirect = searchParams.has('session_token') && searchParams.has('state');

    // Show loading spinner while checking auth and profile
    if (!isReady || loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Stage 1: Check authentication
    if (!authenticated && !isAuth0Redirect) {
        return <Navigate to="/home" state={{ from: location }} replace />;
    }

    // Allow access if Auth0 redirect (user will authenticate after completing form)
    if (!authenticated && isAuth0Redirect) {
        return children;
    }

    // Stage 2: Check if profile already exists
    if (profile && profile.firstName) {
        // User already registered → Skip /register, go to home
        return <Navigate to="/" replace />;
    }

    // Authenticated + No profile → Allow registration
    return children;
};

export default RegisterRoute;
